import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import * as IssuesV2Store from '../../../redux/issuesV2';
import ModalEditDependency from '../../projects/dependencies/ModalEditDependency';
import { useAsyncRequest } from '../../../hooks';
import { get, put } from '../../../redux/api';
import { nuc } from '../../../utils';
import Locator from '../../../../shared/Locator';
import type { OnSaveDependencyState } from '../../projects/dependencies/ModalEditDependency';
import type { RevisionDependency } from '../../../types';
import type { UpdateProjectCorrectionRequest } from '../../../../typings/api';

/**
 * Takes a key+value pair (usually from an Object.fromEntries call)
 * and sets any undefined values to null.
 */
const undefinedEntryToNull = ([key, value]: [string, unknown]) => [key, value === undefined ? null : value];

/**
 * Hook which fetches the revision data for the `issueDetails.source.id`.
 * This isn't handled by <ModalEditDependency />, which expects
 * that the revision is already fetched. Previously this was all in the
 * RevisionStore Reflux store.
 */
function useFetchRevision(packageLocator?: string) {
  //  Memoized because useAsyncRequest will infinite loop if we don't.
  const fetchRevision = useCallback(async () => get<RevisionDependency>(`/api/revisions/${nuc(packageLocator || '')}/?checkDependencyCache=true`), [packageLocator]);
  const { value: revision, isLoading, errors } = useAsyncRequest(fetchRevision);

  return {
    revision,
    errors,
    isLoading,
  };
}

/**
 * Picks specific fields from the project, ensures any undefined ones are null.
 */
const prepareProjectForSaving = (state: OnSaveDependencyState): UpdateProjectCorrectionRequest['project'] => {
  const projectState = _.pick(state, [
    'title',
    'description',
    'url',
    'authors',
    'scm_url',
  ]);
  return Object.fromEntries(Object.entries(projectState).map(undefinedEntryToNull));
};

/**
 * Picks specific fields from each license in the license groups, ensures any undefined ones are null.
 */
const prepareLicensesForSaving = (licenseGroups: OnSaveDependencyState['licenses'] = []): UpdateProjectCorrectionRequest['licenses'] => {
  return licenseGroups.map(group =>
    group.map(license => {
      const licenseState = _.pick(license, [
        'licenseId',
        'title',
        'url',
        'text',
        'copyright',
        'ignored',
        'licenseGroupId',
      ]);
      return Object.fromEntries(Object.entries(licenseState).map(undefinedEntryToNull));
    }),
  );
};

/**
 * Tiny hook to handle onDependencySave by sending a PUT request
 * to `/api/projects/:locator/correction`.
 */
function useSavePackageCorrection(parentRevisionLocator?: string) {
  const dispatch = useDispatch();
  return useCallback(async (packageLocator: string, state: OnSaveDependencyState) => {
    const { fetcher, package: name } = Locator.parse(packageLocator);
    await put<UpdateProjectCorrectionRequest, null>(
      `/api/projects/${nuc(`${fetcher}+${name}`)}/correction`,
      {
        parentRevisionLocator,
        project: prepareProjectForSaving(state),
        licenses: prepareLicensesForSaving(state.licenses),
      },
    );
    return dispatch(IssuesV2Store.actions.refetchIssueDetails());
  }, [dispatch, parentRevisionLocator]);
}

type Props = {
  isOpen: boolean;
  onHide: () => void;
};

/**
 * EditDependencyModal wraps the existing ModalEditDependency
 * component to ensure that we fetch the `revision`.
 *
 * In issuesV1, this was done by the time the issue details had loaded,
 * but in issuesV2 we need to load this separately.
 */
const EditDependencyModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
}) => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const { revision, isLoading, errors } = useFetchRevision(issueDetails?.source.id);
  //  For project-scoped issues, we provide the parent revision locator.
  //  This will return `undefined` if we're not in the projects scope.
  const parentRevisionLocator = useSelector(IssuesV2Store.selectors.getProjectLocatorFromScope);
  const onDependencySave = useSavePackageCorrection(parentRevisionLocator);

  if (!issueDetails) {
    return null;
  }

  return isLoading ? null : (
    <ModalEditDependency
      show={isOpen}
      onHide={onHide}
      // @ts-expect-error -- This is fine because if there is no revision, we still want the modal to open and display any errors.
      revision={revision}
      parentRevisionLocator={parentRevisionLocator}
      onDependencySave={onDependencySave}
      fetchRevisionErrors={errors}
    />
  );
};

export default EditDependencyModal;
